import { X } from 'lucide-react'
import { format } from 'date-fns'

import { useEffect, useRef, useState } from 'react'

import { stringifyGraph } from '@/clients/http'
import { useAuth } from '@/contexts/AuthContext'
import { BranchModel, type BranchOption } from '@/http/models/Branch'
import { ProductModel as ProductApi } from '@/http/models/Product/Product'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'

import { ProductModel as ProductModelTypes, StockHttpParams } from '@/types'

/** One stock batch at the source branch (a purchase), as `stocks` on a variation. */
interface Batch {
  key: string
  product_variation_id: string
  stock_purchase_transaction_id: string
  variationName: string
  available: number
  expiry_date?: string
  quantity: string
}

interface StockRow {
  branch_id: string
  stock_purchase_transaction_id: string
  quantity: number
  expiry_date?: string
  created_at?: string
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (body: StockHttpParams.CreateTransfer['body']) => void
  loading: boolean
}

/**
 * Moves stock between branches (`POST /stock/transfer`, checklist C27). Stock is kept per purchase batch, so the
 * user picks quantities from the source branch's batches; the server keeps each batch's expiry at the destination.
 */
export const TransferDialog = ({ open, onOpenChange, onSubmit, loading }: Props) => {
  const [branches, setBranches] = useState<BranchOption[]>([])
  const [source, setSource] = useState('')
  const [destination, setDestination] = useState('')
  const [description, setDescription] = useState('')
  const [search, setSearch] = useState('')
  const [products, setProducts] = useState<ProductModelTypes.Product[]>([])
  const [searching, setSearching] = useState(false)
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [batches, setBatches] = useState<Batch[]>([])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const canListBranches = useAuth().can('branches:list')

  useEffect(() => {
    if (!open) return
    setSource('')
    setDestination('')
    setDescription('')
    setSearch('')
    setProducts([])
    setBatches([])
    if (canListBranches) BranchModel.getOptions().then((res) => setBranches(res.success ? (res.data?.result ?? []) : []))
  }, [open, canListBranches])

  // Batches belong to the source branch; a new source starts over.
  useEffect(() => setBatches([]), [source])

  const findProducts = (value: string) => {
    setSearch(value)
    if (timer.current) clearTimeout(timer.current)
    if (!value.trim()) {
      setProducts([])
      return
    }
    timer.current = setTimeout(async () => {
      setSearching(true)
      const res = await ProductApi.getList({ query: { search: value, length: 10 } })
      setProducts(res.success ? (res.data?.result ?? []) : [])
      setSearching(false)
    }, 300)
  }

  const addProduct = async (product: ProductModelTypes.Product) => {
    setProducts([])
    setSearch('')
    setLoadingProduct(true)
    const res = await ProductApi.getById({
      params: { id: product._id },
      query: {
        graph_enc: stringifyGraph({
          variations: {
            _id: true,
            name: true,
            'stocks(quantity!=0)': { branch_id: true, stock_purchase_transaction_id: true, quantity: true, expiry_date: true, created_at: true },
          },
        }),
      },
    })
    setLoadingProduct(false)
    const variations = res.success ? ((res.data?.result as unknown as { variations?: { _id: string; name: string; stocks?: StockRow[] }[] })?.variations ?? []) : []
    const found: Batch[] = []
    for (const v of variations) {
      for (const s of v.stocks ?? []) {
        if (String(s.branch_id) !== source || s.quantity <= 0) continue
        const key = `${v._id}:${s.stock_purchase_transaction_id}`
        if (batches.some((b) => b.key === key)) continue
        found.push({
          key,
          product_variation_id: v._id,
          stock_purchase_transaction_id: String(s.stock_purchase_transaction_id),
          variationName: variations.length > 1 ? `${product.name} – ${v.name}` : product.name,
          available: s.quantity,
          expiry_date: s.expiry_date,
          quantity: '',
        })
      }
    }
    setBatches((prev) => [...prev, ...found])
  }

  const update = (key: string, quantity: string) => setBatches((prev) => prev.map((b) => (b.key === key ? { ...b, quantity } : b)))
  const remove = (key: string) => setBatches((prev) => prev.filter((b) => b.key !== key))

  const chosen = batches.filter((b) => Number(b.quantity) > 0)
  const valid =
    !!source &&
    !!destination &&
    source !== destination &&
    chosen.length > 0 &&
    chosen.every((b) => Number(b.quantity) <= b.available)

  const submit = () => {
    if (!valid) return
    onSubmit({
      branch_id: source,
      destination_branch_id: destination,
      ...(description.trim() ? { description: description.trim() } : {}),
      entries: chosen.map((b) => ({
        product_variation_id: b.product_variation_id,
        stock_purchase_transaction_id: b.stock_purchase_transaction_id,
        quantity: Number(b.quantity),
      })),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Transfer stock</DialogTitle>
          <DialogDescription>Move stock from one branch to another. Quantities come out of the source branch's purchase batches.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="transfer-from">From branch</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger id="transfer-from">
                  <SelectValue placeholder="Choose a branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-to">To branch</Label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger id="transfer-to">
                  <SelectValue placeholder="Choose a branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.filter((b) => b._id !== source).map((b) => (
                    <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="transfer-product">Add a product</Label>
            <div className="relative">
              <Input
                id="transfer-product"
                value={search}
                onChange={(e) => findProducts(e.target.value)}
                placeholder={source ? 'Search products' : 'Choose the source branch first'}
                disabled={!source}
              />
              {(searching || loadingProduct) && <Spinner className="absolute right-2 top-1/2 -translate-y-1/2" />}
              {products.length > 0 && (
                <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-dropdown">
                  {products.map((p) => (
                    <li key={p._id}>
                      <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-subdued" onClick={() => addProduct(p)}>
                        {p.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {batches.length > 0 ? (
            <div className="overflow-x-auto rounded-md border border-border-subtle">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="hidden sm:table-cell">Expiry</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead className="w-28 text-right">Transfer</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((b) => {
                    const over = Number(b.quantity) > b.available
                    return (
                      <TableRow key={b.key}>
                        <TableCell className="font-medium">{b.variationName}</TableCell>
                        <TableCell className="hidden text-muted-foreground sm:table-cell">{b.expiry_date ? format(new Date(b.expiry_date), 'd MMM yyyy') : '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">{b.available}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min={0}
                            max={b.available}
                            value={b.quantity}
                            onChange={(e) => update(b.key, e.target.value)}
                            aria-label={`Quantity of ${b.variationName}`}
                            aria-invalid={over}
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" aria-label={`Remove ${b.variationName}`} onClick={() => remove(b.key)}>
                            <X />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            source && <p className="text-sm text-muted-foreground">Products without stock at the source branch are not added.</p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="transfer-note">Note</Label>
            <Textarea id="transfer-note" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Optional" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || loading}>
            {loading && <Spinner />}
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
